import { useTicketRanking, type AssignmentAuditRecord } from '@/hooks/useTicketRanking';
// CORE-G / CORE-AB: what this placement's timing actually rested on, read
// from the snapshot smart-assign wrote at decision time — never re-resolved
// from the technician row, which is mutable.
import {
  readEfficiencyAuditSnapshot,
  describeEfficiencyAudit,
} from '@/utils/assignment/efficiencyAuditSnapshot';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MissingReasonChip } from './MissingReasonChip';
import { 
  Award, 
  AlertTriangle, 
  Ban, 
  CheckCircle2, 
  Info,
  ArrowRightLeft
} from 'lucide-react';

interface TicketRankingPanelProps {
  ticketId: string | null;
  className?: string;
}

export function TicketRankingPanel({ ticketId, className }: TicketRankingPanelProps) {
  const { records, overrideInfo, isLoading, error } = useTicketRanking(ticketId);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive mb-2" />
        <p className="text-sm text-muted-foreground">
          Failed to load ranking data: {error.message}
        </p>
      </div>
    );
  }

  if (!ticketId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Select a ticket to view its ranking
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <span className="text-2xl">📊</span>
        </div>
        <p className="text-sm text-muted-foreground">No assignment scores found</p>
        <p className="text-xs text-muted-foreground mt-1">
          Scores are logged during OptiPlace assignments
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className={className}>
      <div className="px-4 pb-4 space-y-4">
        {/* Header microcopy */}
        <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium text-muted-foreground">
              Assignment Evaluation (Snapshot at Creation)
            </span>
          </div>
          <p className="text-xs text-muted-foreground pl-6">
            This reflects OptiBay's evaluation at the moment the ticket was created. 
            This data does not change after creation. Overrides are logged separately.
          </p>
        </div>

        {/* Override banner */}
        {overrideInfo.isOverridden && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <ArrowRightLeft className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Override Detected</span>
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5 pl-6">
              <p>System Recommendation: <span className="font-medium text-foreground">{overrideInfo.systemRecommendedTech}</span></p>
              <p>Final Assignment: <span className="font-medium text-foreground">
                {records.find(r => r.tech_id === overrideInfo.finalAssignedTechId)?.tech_name || 'Unknown'}
              </span></p>
            </div>
          </div>
        )}

        {/* Technician cards */}
        {records.map((record, index) => (
          <TechRankingCard 
            key={record.id} 
            record={record} 
            rank={index + 1}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

function TechRankingCard({ record, rank }: { record: AssignmentAuditRecord; rank: number }) {
  const isSelected = record.selected;
  const isRejected = !!record.rejection_reason;
  const details = (record.explainability_details || {}) as Record<string, unknown>;
  const missingReasons = (details.missing || {}) as Record<string, string | null>;
  const overallScore = typeof details.overall_score === 'number' ? details.overall_score : record.fitness_score;

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${
      isSelected 
        ? 'border-green-500/40 bg-green-500/5' 
        : isRejected 
          ? 'border-destructive/20 bg-destructive/5 opacity-75'
          : 'border-border bg-card'
    }`}>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-muted-foreground w-5">#{rank}</span>
          {isSelected && <Award className="h-4 w-4 text-green-600" />}
          <span className="font-medium text-sm">{record.tech_name}</span>
          {record.tech_skill_tier != null && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              T{record.tech_skill_tier}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRejected ? (
            <Badge variant="destructive" className="text-xs gap-1">
              <Ban className="h-3 w-3" />
              Ineligible
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs gap-1 text-green-600 border-green-500/30">
              <CheckCircle2 className="h-3 w-3" />
              Eligible
            </Badge>
          )}
          {overallScore != null && (
            <Badge variant="secondary" className="font-mono text-xs">
              {typeof overallScore === 'number' ? overallScore : Number(overallScore).toFixed(0)}
            </Badge>
          )}
        </div>
      </div>

      {/* Rejection reason */}
      {isRejected && (
        <p className="text-xs text-destructive pl-7">{record.rejection_reason}</p>
      )}

      {/* Score breakdown grid - only for eligible techs */}
      {!isRejected && (
        <div className="grid grid-cols-3 gap-x-4 gap-y-1 pl-7 text-xs">
          <ScoreField label="Proficiency" value={record.avg_proficiency} suffix="%" />
          <ScoreField label="Proficiency Score" value={record.proficiency_score} />
          <ScoreField label="Workload" value={record.workload_score} />
          <ScoreField label="Efficiency" value={record.efficiency_score} missingCode={missingReasons.efficiency_score} />
          <ScoreField label="Idle Gap" value={record.idle_gap_score} missingCode={missingReasons.idle_gap_score} />
          <ScoreField label="Complexity" value={record.complexity_weight} />
          <ScoreField label="Bay Match" value={record.bay_match != null ? (record.bay_match ? 'Yes' : 'No') : null} />
          <ScoreField label="Learned Time" value={record.learned_time_minutes} suffix="m" missingCode={missingReasons.learned_time_minutes} />
          <ScoreField label="Time Source" value={record.learned_time_source} missingCode={missingReasons.learned_time_source} />
          <ScoreField label="Buffer Impact" value={record.buffer_impact_minutes} suffix="m" missingCode={missingReasons.buffer_impact_minutes} />
          <ScoreField label="Comeback Bonus" value={record.comeback_bonus} isBonus missingCode={missingReasons.comeback_bonus} />
          <ScoreField label="Specialist Pen." value={record.specialist_penalty} isPenalty missingCode={missingReasons.specialist_penalty} />
          <ScoreField label="Tier Gap Pen." value={record.tier_gap_penalty} isPenalty />
          <ScoreField label="Diag Quality" value={record.diagnostic_quality_bonus} isBonus missingCode={missingReasons.diagnostic_quality_bonus} />
          <ScoreField label="DCS" value={record.tech_dcs} missingCode={missingReasons.tech_dcs} />
        </div>
      )}

      {/* Explainability details */}
      {/*
        CORE-G / CORE-AB — the efficiency basis this placement used.

        `efficiency_score` beside it is a bare number. Without this line an
        audit read a year from now cannot tell a 125 learned from forty visits
        from a 125 nobody classified from an invented 100 — and all three lead
        to different conclusions about whether the shop should trust the
        timing it promised.

        Rendered for rejected candidates too: a technician excluded on a
        timing that rested on an invented efficiency is exactly the case worth
        being able to find later.
      */}
      {(() => {
        const reading = describeEfficiencyAudit(
          readEfficiencyAuditSnapshot(record.explainability_details),
        );
        return (
          <div
            className="pl-7 text-xs"
            data-testid="audit-efficiency-basis"
            data-basis={reading.basis ?? 'not_recorded'}
            data-fallback={reading.isFallback ? 'true' : 'false'}
            title={reading.detail}
          >
            <span className="text-muted-foreground">Efficiency basis: </span>
            <span className={reading.isEstablished ? 'font-medium' : 'italic'}>
              {reading.label}
            </span>
          </div>
        );
      })()}
      {!isRejected && record.explainability_details && (
        <ExplainabilitySection details={record.explainability_details} />
      )}
    </div>
  );
}

function ScoreField({ 
  label, 
  value, 
  suffix = '', 
  isBonus = false, 
  isPenalty = false,
  missingCode,
}: { 
  label: string; 
  value: number | string | null | undefined; 
  suffix?: string; 
  isBonus?: boolean; 
  isPenalty?: boolean;
  missingCode?: string | null;
}) {
  if (value == null) return (
    <div>
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-1">
        <MissingReasonChip reasonCode={missingCode} />
      </span>
    </div>
  );

  let colorClass = 'text-foreground';
  if (isBonus && typeof value === 'number' && value < 0) colorClass = 'text-green-600 dark:text-green-400';
  if (isPenalty && typeof value === 'number' && value > 5) colorClass = 'text-red-600 dark:text-red-400';

  const displayValue = typeof value === 'number' ? value.toFixed(1) : value;

  return (
    <div>
      <span className="text-muted-foreground">{label}</span>
      <span className={`ml-1 font-mono ${colorClass}`}>{displayValue}{suffix}</span>
    </div>
  );
}

function ExplainabilitySection({ details }: { details: Record<string, unknown> }) {
  // `efficiency` is an object and is rendered by its own line above; left in
  // here it would print as "[object Object]", which is worse than the gap it
  // was added to close.
  const excludedKeys = new Set(['overall_score', 'missing', 'efficiency']);
  const entries = Object.entries(details).filter(([k, v]) => v != null && !excludedKeys.has(k));
  if (entries.length === 0) return null;

  return (
    <div className="pl-7 pt-1 border-t border-border/50">
      <span className="text-xs text-muted-foreground font-medium">Details</span>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs">
        {entries.map(([key, val]) => (
          <div key={key}>
            <span className="text-muted-foreground">{key.replace(/_/g, ' ')}: </span>
            <span className="font-mono text-foreground">{String(val)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
